import { useState } from "react";
import { Box, CircularProgress, Grid, IconButton, Typography } from "@mui/material";
import {
  AssignmentTurnedInOutlined,
  EmailOutlined,
  InsertDriveFileOutlined,
  NoteOutlined,
  PrintOutlined,
  QuestionAnswerOutlined,
  SendOutlined,
  TextsmsOutlined,
  UnfoldLess,
  UnfoldMore,
  WorkOutlineOutlined,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { stripHtml } from "string-strip-html";
import CircleIcon from "../../CircleIcon/CircleIcon";
import useHistoryLogEntity from "../../../hooks/useHistoryLogEntity";
import {
  ActivityHistoryLog,
  EntityBackedHistoryLog,
  FaxHistoryEntity,
  HistoryLogEntityMap,
} from "../../../types/HistoryLog";
import { getEntityHistoryPreview, getEntityHistoryTitle } from "../../../services/historyService";
import { translate } from "../../../services/Translations/translatorService";
import { ProductUnion } from "../../../types/ProductUnion";
import { Activity } from "../../../types/Activity";
import ActivityNote from "../../Activities/ActivityNote";
import CaseHistoryActivityCalendlyAssessment from "./CustomCaseHistoryItem/CaseHistoryActivityCalendlyAssessment";
import { BeaMessage } from "../../../types/Bea/BeaMessage";
import { EmailMessage } from "../../../types/EmailMessage";
import { InquiryToCustomer } from "../../../types/InquiryToCustomer";
import { MediaObject } from "../../../types/MediaObject";
import MediaObjectChips from "../../MediaObjectSection/MediaObjectChips";
import { Message } from "../../../types/Messaging/Message";
import Note from "../CaseNote/Note";
import { Note as NoteType } from "../../../types/Note";
import PaidTaskHistoryLog from "./CustomCaseHistoryItem/PaidTaskHistoryLog";
import { PaidTask } from "../../../types/PaidTask";
import caseHistoryItemStyle from "./caseHistoryItemStyle";
import entityHistoryLogEntryStyles from "./entityHistoryLogEntryStyles";
import { getActivityLabel } from "../../../services/Activities/activityTypes";
import { useBackofficeUser } from "../../../provider/BackofficeUserProvider";

type EntityHistoryLogEntryProps = {
  historyItem: EntityBackedHistoryLog;
  product: ProductUnion;
};

const getIcon = (historyItem: EntityBackedHistoryLog) => {
  switch (historyItem.logData.entityType) {
    case "activity":
      return <AssignmentTurnedInOutlined />;
    case "beaMessage":
      return <SendOutlined />;
    case "emailMessage":
      return <EmailOutlined />;
    case "fax":
      return <PrintOutlined />;
    case "inquiryToCustomer":
      return <QuestionAnswerOutlined />;
    case "mediaObject":
      return <InsertDriveFileOutlined />;
    case "message":
      return <TextsmsOutlined />;
    case "note":
      return <NoteOutlined />;
    case "paidTask":
      return <WorkOutlineOutlined />;
  }
};

const InquiryDetails = ({ inquiry }: { inquiry: InquiryToCustomer }) => {
  const requestedDocuments = inquiry.requestedDocuments ?? [];
  const plainText = (value: string) => stripHtml(value).result;
  const details = [
    requestedDocuments.length > 0 &&
      `Benötigte Dokumente: ${requestedDocuments.map((document) => document.documentName).join(", ")}`,
    inquiry.inquiryTextAppointment && `Hintergrund für Gespräch: ${plainText(inquiry.inquiryTextAppointment)}`,
    inquiry.inquiryTextAppointmentCustomer &&
      `Begründung zur Ablehnung: ${plainText(inquiry.inquiryTextAppointmentCustomer)}`,
    inquiry.inquiryTextInformation && `Benötigte Informationen: ${plainText(inquiry.inquiryTextInformation)}`,
    inquiry.inquiryTextInformationCustomer &&
      `Antwort Mandantschaft: ${plainText(inquiry.inquiryTextInformationCustomer)}`,
    inquiry.inquiryTextDocuments && `Erklärung zu Dokumenten: ${plainText(inquiry.inquiryTextDocuments)}`,
    inquiry.inquiryTextDocumentsCustomer &&
      `Anmerkungen Mandantschaft: ${plainText(inquiry.inquiryTextDocumentsCustomer)}`,
    inquiry.inquiryDecisionQuestion && `Frage: ${plainText(inquiry.inquiryDecisionQuestion)}`,
    inquiry.inquiryDecisionAnswer &&
      `Antwort Mandantschaft: ${translate(`historyItem.inquiry.decision.answer.${inquiry.inquiryDecisionAnswer}.label`)}`,
  ].filter((detail): detail is string => Boolean(detail));

  return (
    <Box sx={{ whiteSpace: "pre-line" }}>
      <Typography>
        Status: {translate(`historyItem.inquiry.status.${inquiry.statusText}`, inquiry.statusText)}
      </Typography>
      {details.length > 0 ? details.join("\n\n") : translate("historyItem.details.emptySummary")}
    </Box>
  );
};

const EntityDetails = ({
  historyItem,
  entity,
  product,
}: {
  historyItem: EntityBackedHistoryLog;
  entity: HistoryLogEntityMap[keyof HistoryLogEntityMap];
  product: ProductUnion;
}) => {
  const { getUserData } = useBackofficeUser();

  switch (historyItem.logData.entityType) {
    case "activity": {
      const activity = entity as Activity<string>;
      const activityHistoryItem = historyItem as ActivityHistoryLog;
      const assignedUserName = getUserData(activity.assignedUser)?.person.fullname ?? "Nicht zugewiesen";
      const creatorName = getUserData(activity.creator ?? null)?.person.fullname ?? null;
      const calendlyAssessment =
        activity.calendlyEvent && typeof activity.calendlyEvent === "object"
          ? (activity.calendlyEvent.assessment ?? null)
          : null;
      const eventUserName = activityHistoryItem.backofficeUser?.person?.fullname ?? "System";
      const eventType = activityHistoryItem.logData.snapshot.activityEventType;
      let status = "Offen";
      if (activity.deleted) {
        status = "Gelöscht";
      } else if (activity.done) {
        status = "Erledigt";
      }
      const statusDate = activity.deleted
        ? (activity.deletedTime ?? activityHistoryItem.logDate)
        : (activity.doneTime ?? null);

      return (
        <Box>
          <Typography>Typ: {getActivityLabel(product.productClassName, activity.type)}</Typography>
          <Typography>
            Fällig: {activity.dueDate ? dayjs(activity.dueDate).format("DD.MM.YYYY") : "Kein Datum"}
            {activity.dueTime && activity.dueTime !== "allDay" ? ` um ${activity.dueTime} Uhr` : ""}
          </Typography>
          <Typography>Zu erledigen durch: {assignedUserName}</Typography>
          <Typography>
            Status: {status}
            {statusDate ? ` am ${dayjs(statusDate).format("DD.MM.YYYY HH:mm")} Uhr` : ""}
          </Typography>
          {activity.created && (
            <Typography>
              Erstellt am: {dayjs(activity.created).format("DD.MM.YYYY HH:mm")} Uhr | {creatorName ?? "Unbekannt"}
            </Typography>
          )}
          {eventType === "deleted" && <Typography>Gelöscht durch: {eventUserName}</Typography>}
          <Typography>
            Dauer: {activity.duration ?? 0} Minuten |{" "}
            {activity.mandatory ? "Pflichtaktivität" : "Keine Pflichtaktivität"}
          </Typography>
          <ActivityNote noteContent={activity.note} />
          {calendlyAssessment && (
            <CaseHistoryActivityCalendlyAssessment calendlyAssessment={calendlyAssessment} />
          )}
        </Box>
      );
    }
    case "beaMessage": {
      const beaMessage = entity as BeaMessage;

      return (
        <Box>
          <Typography>
            {beaMessage.sender?.firstname} {beaMessage.sender?.surname} {"→"} {beaMessage.recipient?.firstname}{" "}
            {beaMessage.recipient?.surname}
          </Typography>
          {beaMessage.message && <Typography>{beaMessage.message}</Typography>}
        </Box>
      );
    }
    case "emailMessage": {
      const emailMessage = entity as EmailMessage;
      const content = emailMessage.messageContent.html || emailMessage.messageContent.text;

      return (
        <Box>
          {emailMessage.from && emailMessage.to && (
            <Typography>
              {emailMessage.from.email} {"→"} {emailMessage.to.email}
            </Typography>
          )}
          <iframe
            title={`history-email-${emailMessage.id}`}
            srcDoc={content ?? translate("historyItem.details.emptySummary")}
            sandbox=""
            style={{ width: "100%", border: "none", minHeight: "12rem" }}
          />
        </Box>
      );
    }
    case "fax": {
      const fax = entity as FaxHistoryEntity;

      return (
        <Typography>
          {historyItem.logData.snapshot.mediaObjectName ?? fax.mediaObject} |{" "}
          {translate(`fax.status.values.${fax.status}`, fax.status)}
        </Typography>
      );
    }
    case "inquiryToCustomer":
      return <InquiryDetails inquiry={entity as InquiryToCustomer} />;
    case "mediaObject": {
      const mediaObject = entity as MediaObject;

      return (
        <Box>
          {mediaObject.description && <Typography>{mediaObject.description}</Typography>}
          {mediaObject.fieldname && <Typography>Art des Dokuments: {mediaObject.fieldname}</Typography>}
          <MediaObjectChips mediaObject={mediaObject} showFolder />
        </Box>
      );
    }
    case "message":
      return (
        <Box dangerouslySetInnerHTML={{ __html: (entity as Message).messageText ?? "" }} sx={{ marginTop: "0.5rem" }} />
      );
    case "note":
      return <Note note={entity as NoteType} product={product} isEditable={false} />;
    case "paidTask":
      return <PaidTaskHistoryLog paidTask={entity as PaidTask} />;
  }
};

export default function EntityHistoryLogEntry({ historyItem, product }: EntityHistoryLogEntryProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { entityType, entityId } = historyItem.logData;
  const { data: entity, isLoading, isError } = useHistoryLogEntity(entityType, entityId, isExpanded);
  const preview = getEntityHistoryPreview(historyItem);
  const missingDetailsText =
    historyItem.type === "mediaObjectDelete"
      ? translate("historyItem.details.deletedDocument")
      : translate("historyItem.details.unavailable");

  return (
    <Grid container wrap="nowrap">
      <Grid sx={entityHistoryLogEntryStyles.icon}>
        <CircleIcon icon={getIcon(historyItem)} type="muiIcon" />
      </Grid>
      <Grid sx={entityHistoryLogEntryStyles.content}>
        <Grid container sx={entityHistoryLogEntryStyles.header}>
          <Grid sx={{ flexGrow: 1 }}>
            <Box
              sx={
                historyItem.type === "mediaObjectDelete"
                  ? caseHistoryItemStyle.headlineLineThrough
                  : caseHistoryItemStyle.headline
              }
            >
              {getEntityHistoryTitle(historyItem)}
            </Box>
            {preview && <Box sx={caseHistoryItemStyle.info}>{preview}</Box>}
            <Box sx={caseHistoryItemStyle.info}>
              {dayjs(historyItem.logDate).format("DD.MM.YYYY HH:mm")} Uhr |{" "}
              {historyItem.backofficeUser?.person?.fullname ?? "System"}
            </Box>
          </Grid>
          {entityId !== null && (
            <Grid>
              <IconButton onClick={() => setIsExpanded(!isExpanded)}>
                {isExpanded ? <UnfoldLess /> : <UnfoldMore />}
              </IconButton>
            </Grid>
          )}
        </Grid>
        {entityId === null && <Box sx={caseHistoryItemStyle.info}>{missingDetailsText}</Box>}
        {isExpanded && (
          <Box sx={entityHistoryLogEntryStyles.details}>
            {isLoading && <CircularProgress size="1.5rem" />}
            {isError && <Typography>{missingDetailsText}</Typography>}
            {entity && <EntityDetails historyItem={historyItem} entity={entity} product={product} />}
          </Box>
        )}
      </Grid>
    </Grid>
  );
}
